import React, { useState, useEffect } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from 'gatsby';
import { useCart } from '@printdeal/checkout-frontend';
// @ts-expect-error
import { Login } from '@printdeal/authentication-component';
import { Alert } from '@printdeal/ui-library';
import { ErrorManagement } from '@printdeal/error-management/react';
import { selectors as authSelectors, actions as authActions } from '../../store/slices/authSlice';
import { portalId } from '../../helpers/portal';
import { dispatchDataLayerEventLoginError } from '../../helpers/dataLayerHelpers/LoginDataLayerHelper';
// @ts-expect-error
import { AuthDataLayerHelper } from '../../helpers/dataLayerHelpers/AuthDataLayerHelper';
import { messages } from '../../translations/login';
import useSystemPages from '../../hooks/useSystemPages';
import { Location } from '../../types/Location';

const STATUS = {
  ERROR: 'error',
  INITIAL: 'initial',
  LOADING: 'loading',
  SUCCESS: 'success',
};

interface LoginFormProps {
  setIsModalOpen?: (arg0: boolean) => void;
  useLoginSlideOut: boolean;
  eventLabel?: string;
  redirectUrlAfterLogin?: string;
}

const LoginForm = ({
  setIsModalOpen,
  useLoginSlideOut,
  eventLabel,
  redirectUrlAfterLogin,
}: LoginFormProps) => {
  const dispatch = useDispatch();
  const location: Location = useLocation() as Location;
  const authToken = useSelector(authSelectors.getAuthToken);
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const isLoading = useSelector(authSelectors.isAuthLoading);
  const { cartId } = useCart();
  const [status, setStatus] = useState(STATUS.INITIAL);
  const [errorMessage, setErrorMessage] = useState(messages.defaultError);
  const [
    loginPage,
    accountDashboardPage,
    registrationPage,
    cartPage,
    deliveryPage,
    segmentedCustomerLandingPage,
  ] = useSystemPages('login', 'accountDashboard', 'registration', 'cart', 'delivery', 'customerProgramsReseller');

  useEffect(() => {
    if (isLoggedIn && setIsModalOpen) {
      setIsModalOpen(false);
    }
  }, [isLoggedIn]);

  const getErrorMessage = (reason: string) => {
    if (reason === 'LOGIN_MIGRATED') {
      return messages.loginMigrated;
    }
    if (reason === 'INVALID_EMAIL') {
      return messages.invalidEmail;
    }
    if (reason === 'INVALID_PASSWORD') {
      return messages.invalidPassword;
    }
    return messages.defaultError;
  };

  const handleCallback = async (
    error: { toString: () => unknown; },
    response: { success: boolean; account: { id: string; }; reason: string; },
  ) => {
    if (response && response.success) {
      setStatus(STATUS.SUCCESS);

      AuthDataLayerHelper.dispatchLoginSuccessDataLayerEvent(
        `${eventLabel ? 'Login' : 'LoginSlideOut'} - ${location.pathname}`,
        response.account.id,
        eventLabel,
      );

      let redirectUrl = location.pathname;

      if (location.pathname === loginPage.path || location.pathname === registrationPage.path) {
        if (location.state?.redirectUrl === cartPage.path || location.state?.redirectUrl === deliveryPage.path) {
          redirectUrl = location.state.redirectUrl;
        } else {
          redirectUrl = accountDashboardPage.path;
        }
      } else {
        redirectUrl = accountDashboardPage.path;
      }

      // Override redirect URL from prop
      if (redirectUrlAfterLogin) {
        redirectUrl = redirectUrlAfterLogin;
      }

      if (location.pathname === segmentedCustomerLandingPage.path) {
        redirectUrl = location.pathname;
      }

      dispatch(
        // @ts-expect-error
        authActions.login(response),
      );

      if (setIsModalOpen) {
        setIsModalOpen(false);
      }

      navigate(
        redirectUrl,
      );
    } else {
      setStatus(STATUS.ERROR);

      if (response?.reason === 'INVALID_EMAIL') {
        setErrorMessage(getErrorMessage('INVALID_EMAIL'));
        dispatchDataLayerEventLoginError('invalidEmail');
      } else if (response?.reason === 'INVALID_PASSWORD') {
        setErrorMessage(getErrorMessage('INVALID_PASSWORD'));
        dispatchDataLayerEventLoginError('invalidPassword');
      } else if (response?.reason === 'LOGIN_MIGRATED') {
        setErrorMessage(getErrorMessage('LOGIN_MIGRATED'));
        dispatchDataLayerEventLoginError('loginMigrated');
      } else {
        setErrorMessage(getErrorMessage('OTHER'));
        ErrorManagement.exception({
          exception: 'Unsuccessful login with unknown reason',
          extra: {
            loginResponse: JSON.stringify(response),
            loginError: error instanceof Error ? error.toString() : JSON.stringify(error),
          },
          tags: {
            fileLocation: 'LoginForm.tsx',
            featureFunction: 'handleCallback',
          },
          fingerprint: ['login-handleCallback-slideout'],
        });

        dispatchDataLayerEventLoginError('defaultError');
      }
    }
  };

  return (
    <>
      {status === STATUS.ERROR && (
        <Alert variant="error">
          <FormattedMessage id={errorMessage.id} defaultMessage={errorMessage.defaultMessage} />
        </Alert>
      )}
      <Login
        store={process.env.GATSBY_STORE}
        portalId={portalId}
        callback={handleCallback}
        endpoint={`${process.env.GATSBY_AUTH_ENDPOINT}/login`}
        authToken={authToken}
        cartId={cartId}
        useLoginSlideOut={useLoginSlideOut}
        isLoading={isLoading}
        eventLabel={eventLabel}
      />
    </>
  );
};

export default LoginForm;
